import { ArrowUpRight, ExternalLink, Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { useLang } from "../LanguageContext";
import { Chapter, Reveal } from "./Chapter";

const Contact = () => {
  const { t, lang } = useLang();
  const c = t.contact;
  const mapsLabel = lang === "it" ? "Apri in Google Maps" : "Open in Google Maps";

  return (
    <Chapter id="contatti" num={c.num} kicker={c.kicker} title={c.title} dark>
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(55% 45% at 12% 90%, rgba(200,109,81,0.12), transparent 70%)" }}
      />
      <div className="relative">
        <Reveal>
          <p className="-mt-8 mb-14 max-w-xl text-base font-light text-linen/70 md:-mt-12">{c.sub}</p>
        </Reveal>

        <div className="grid gap-5 lg:grid-cols-3">
          <Reveal>
            <a
              data-testid="contact-phone-link"
              href="tel:+393317642541"
              className="group flex h-full flex-col border border-linen/15 p-8 transition-colors duration-500 hover:border-gold/50 hover:bg-olive sm:p-10"
            >
              <Phone className="h-6 w-6 text-gold" strokeWidth={1.25} />
              <span className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-linen/50">{c.phoneLabel}</span>
              <span className="mt-2 font-serif text-2xl font-light tracking-tight sm:text-3xl">+39 331 764 2541</span>
              <ArrowUpRight className="mt-auto h-4 w-4 pt-0 text-linen/40 transition-all duration-300 group-hover:text-gold" strokeWidth={1.5} style={{ marginTop: "2rem" }} />
            </a>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              data-testid="contact-email-link"
              href="mailto:eros.zannoni@gmail.com"
              className="group flex h-full flex-col border border-linen/15 p-8 transition-colors duration-500 hover:border-gold/50 hover:bg-olive sm:p-10"
            >
              <Mail className="h-6 w-6 text-gold" strokeWidth={1.25} />
              <span className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-linen/50">{c.emailLabel}</span>
              <span className="mt-2 break-all font-serif text-xl font-light tracking-tight sm:text-2xl">eros.zannoni@gmail.com</span>
              <ArrowUpRight className="h-4 w-4 text-linen/40 transition-colors duration-300 group-hover:text-gold" strokeWidth={1.5} style={{ marginTop: "2rem" }} />
            </a>
          </Reveal>
          <Reveal delay={0.2}>
            <a
              data-testid="contact-whatsapp-link"
              href="https://api.whatsapp.com/send?phone=393317642541"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-full flex-col bg-terra p-8 transition-colors duration-500 hover:bg-gold hover:text-forest sm:p-10"
            >
              <MessageCircle className="h-6 w-6" strokeWidth={1.25} />
              <span className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] opacity-70">WhatsApp</span>
              <span className="mt-2 font-serif text-2xl font-light leading-snug tracking-tight sm:text-3xl">{c.whatsapp}</span>
              <ArrowUpRight className="h-4 w-4 opacity-50 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} style={{ marginTop: "2rem" }} />
            </a>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <h3 className="mt-20 font-serif text-2xl font-light tracking-tight sm:text-3xl">{c.locationsTitle}</h3>
          <p className="mt-3 text-sm font-light text-linen/60">{c.locationsSub}</p>
        </Reveal>
        <div className="mt-8 grid gap-px border border-linen/10 bg-linen/10 sm:grid-cols-2 lg:grid-cols-3">
          {c.locations.map((loc, i) => (
            <Reveal key={i} delay={i * 0.05} y={20}>
              <a
                data-testid={`location-card-${i + 1}`}
                href={loc.map}
                target="_blank"
                rel="noopener noreferrer"
                className="group block h-full bg-forest p-7 transition-colors duration-500 hover:bg-olive"
              >
                <div className="flex items-center justify-between gap-2.5">
                  <div className="flex items-center gap-2.5">
                    <MapPin className="h-3.5 w-3.5 text-terra" strokeWidth={1.5} />
                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{loc.city}</span>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 text-linen/30 transition-colors duration-300 group-hover:text-gold" strokeWidth={1.5} />
                </div>
                <p className="mt-4 font-serif text-lg font-light leading-snug text-linen">{loc.name}</p>
                <p className="mt-2 text-xs font-light leading-relaxed text-linen/60">{loc.addr}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-linen/40 transition-colors duration-300 group-hover:text-gold">
                  {mapsLabel}
                </span>
              </a>
            </Reveal>
          ))}
        </div>
      </div>
    </Chapter>
  );
};

export default Contact;
